// Customer Assist live-session hook — shared by both the agent dialog and
// the client-facing /assist page.
//
// Transport model (revised after first-message-loss bug):
//   - Plain chat (text + attachments) is PERSISTED to the
//     `customer_assist_messages` table on send (migration 048), THEN
//     broadcast on the `assist:<session_id>` Realtime channel. The
//     broadcast is the real-time notification; the DB row is the
//     durable safety net. When a peer subscribes (or reconnects, or
//     refreshes the tab), they hydrate the chat scroller from the
//     table — so a message sent before the other side joined doesn't
//     disappear.
//   - Sensitive content (2FA codes via sendCode) stays BROADCAST-ONLY.
//     Per the locked decision, the value never touches the DB. We do
//     persist a redacted audit line ("[code shared — not retained]")
//     so both sides have a chat-history record that the exchange
//     happened, but the value itself only ever transits via the
//     Realtime broadcast and lives in the receiver's React state for
//     60 seconds.
//   - Dedupe by row id: a hydrated row + an inbound broadcast with the
//     same id is treated as one message (whichever arrives first
//     wins; the second is silently dropped).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';

export type AssistRole = 'agent' | 'client';

export interface AttachmentMeta {
  /** Public storage URL — the bytes are in the `assist-attachments`
   *  bucket under `<session_id>/<ts>-<filename>`. */
  url: string;
  /** Original filename as the uploader saw it. */
  filename: string;
  /** MIME type; image/* renders inline, everything else as a file pill. */
  contentType: string;
  size: number;
}

export interface AssistChatMessage {
  /** UUID of the `customer_assist_messages` row. Drives dedup between
   *  hydrate + broadcast deliveries. */
  id: string;
  from: AssistRole;
  /** Plain-text body. Empty string is valid for attachment-only messages. */
  text: string;
  attachments: AttachmentMeta[];
  /** ISO 8601 — created_at from the DB row. */
  ts: string;
}

export interface AssistCodeRequest {
  id: string;
  label: string;
  ts: string;
}

export interface AssistSensitiveReceived {
  id: string;
  value: string;
  receivedAt: number;
}

export interface AssistPeer {
  role: AssistRole;
  display_name: string;
  joined_at: string;
}

export interface UseAssistChannelResult {
  peers: AssistPeer[];
  messages: AssistChatMessage[];
  codeRequest: AssistCodeRequest | null;
  sensitiveReceived: AssistSensitiveReceived | null;
  connected: boolean;
  ended: boolean;
  /** Set while sending — UI uses it to disable the input briefly so
   *  double-Enter doesn't double-insert. */
  sending: boolean;
  /** When the OTHER side is in the middle of ending the session, this
   *  is the epoch-ms timestamp at which the session will actually
   *  close (10 seconds in the future). Drives the "Closing in N seconds"
   *  countdown + Cancel/Wait button on the receiving side. Null when
   *  no end is in flight. Cleared on cancel or when `ended` flips. */
  endingAt: number | null;
  /** Role of the side that initiated the ending (so the receiver can
   *  show "Agent is ending…" / "Client is ending…" copy). */
  endingFrom: AssistRole | null;
  /** Epoch-ms timestamp of the most recent `end_cancel` payload we
   *  RECEIVED from the other side (NOT a cancel we sent ourselves —
   *  the channel self-suppresses our own broadcasts).
   *
   *  Critical for the agent: when an agent broadcasts `ending`, their
   *  own `endingAt` stays null because they don't receive their own
   *  payload. So watching for `endingAt → null` would never fire when
   *  the client clicks Cancel/Wait. Instead we watch THIS field for
   *  changes. The provider's setTimeout listens here and aborts the
   *  pending end when this updates. */
  endCancelReceivedAt: number | null;
  /** Send a chat message. Persists FIRST (so the message survives a
   *  late subscriber), then broadcasts. Empty text + empty attachments
   *  is a no-op. Throws on insert failure. */
  sendChat: (args: { text: string; attachments?: AttachmentMeta[] }) => Promise<void>;
  /** Agent only — broadcasts a code request + persists a redacted
   *  audit line. */
  sendCodeRequest: (label: string) => Promise<void>;
  /** Client only — broadcasts the sensitive value (NOT persisted) +
   *  persists a redacted audit line ("[code shared — not retained]"). */
  sendCode: (value: string) => Promise<void>;
  /** Broadcast a "session is ending in N ms" signal so the OTHER side
   *  can render a countdown + Cancel button. Does NOT yet end the
   *  session — the caller is responsible for either calling sendEnd()
   *  after the grace window expires, or sendEndCancel() if the user
   *  bails. */
  sendEnding: (graceMs: number) => void;
  /** Broadcast a "never mind, keep the session open" signal. Clears
   *  the pending end on both sides. Idempotent. */
  sendEndCancel: () => void;
  /** Broadcasts an end signal to the other side and flips local
   *  `ended` true. The session row itself is closed by the workflow-17
   *  `end` action separately. */
  sendEnd: () => void;
}

interface ChatPayload {
  kind: 'chat';
  id: string;
  from: AssistRole;
  text: string;
  attachments: AttachmentMeta[];
  ts: string;
}
interface CodeReqPayload {
  kind: 'code_request';
  /** Audit message id (the persisted "[requested X]" row). */
  id: string;
  from: AssistRole;
  label: string;
  text: string;
  ts: string;
}
interface CodePayload {
  kind: 'code';
  /** Audit message id (the persisted "[code shared — not retained]" row). */
  id: string;
  from: AssistRole;
  value: string;
  text: string;
  ts: string;
}
interface EndPayload {
  kind: 'end';
  from: AssistRole;
  ts: string;
}
/** "Heads up, I'm about to end the session in `graceMs`." Sent when
 *  the agent (or client) clicks End — gives the other side time to
 *  cancel via a Wait/Cancel button before the real `end` fires. */
interface EndingPayload {
  kind: 'ending';
  from: AssistRole;
  /** Epoch ms at which the real end will fire if not cancelled. The
   *  sender computes this so receivers can sync their countdowns
   *  without clock drift. */
  endsAt: number;
  ts: string;
}
/** "Never mind, keep chatting." Cancels a pending `ending` signal. */
interface EndCancelPayload {
  kind: 'end_cancel';
  from: AssistRole;
  ts: string;
}
type BroadcastPayload =
  | ChatPayload
  | CodeReqPayload
  | CodePayload
  | EndPayload
  | EndingPayload
  | EndCancelPayload;

interface PresencePayload {
  role: AssistRole;
  display_name: string;
  joined_at: string;
}

interface PersistedRow {
  id: string;
  session_id: string;
  from_role: AssistRole;
  body: string;
  attachments: AttachmentMeta[] | null;
  created_at: string;
}

const SENSITIVE_TTL_MS = 60_000;

function rowToMessage(row: PersistedRow): AssistChatMessage {
  return {
    id: row.id,
    from: row.from_role,
    text: row.body ?? '',
    attachments: Array.isArray(row.attachments) ? row.attachments : [],
    ts: row.created_at,
  };
}

function mergeUniqueById(
  prev: AssistChatMessage[],
  next: AssistChatMessage,
): AssistChatMessage[] {
  if (prev.some((m) => m.id === next.id)) return prev;
  // Keep chronological order by appending — every new row is either the
  // freshest (just inserted) or hydrated in order from the DB, where we
  // already sort ascending by created_at.
  return [...prev, next];
}

export function useAssistChannel(args: {
  client: SupabaseClient;
  sessionId: string | null | undefined;
  role: AssistRole;
  display_name: string;
  enabled?: boolean;
}): UseAssistChannelResult {
  const { client, sessionId, role, display_name } = args;
  const enabled = args.enabled !== false;

  const [peers, setPeers] = useState<AssistPeer[]>([]);
  const [messages, setMessages] = useState<AssistChatMessage[]>([]);
  const [codeRequest, setCodeRequest] = useState<AssistCodeRequest | null>(null);
  const [sensitiveReceived, setSensitiveReceived] = useState<AssistSensitiveReceived | null>(null);
  const [connected, setConnected] = useState(false);
  const [ended, setEnded] = useState(false);
  const [sending, setSending] = useState(false);
  // When the OTHER side broadcasts an `ending` signal, we mirror its
  // `endsAt` here so the receiver can render a "closing in Ns"
  // countdown. Cleared on `end_cancel` or when `ended` flips true.
  // Self-suppression in the listener means an agent who clicks End
  // tracks their own pending state in the provider, not here.
  const [endingAt, setEndingAt] = useState<number | null>(null);
  const [endingFrom, setEndingFrom] = useState<AssistRole | null>(null);
  // Strictly-increasing timestamp updated every time we RECEIVE an
  // `end_cancel` payload (not when we send one). Used by the
  // provider to detect "the client just hit Wait" — necessary
  // because the agent's own `ending` broadcast is self-suppressed,
  // so watching `endingAt → null` would never fire on the cancel
  // initiator's side.
  const [endCancelReceivedAt, setEndCancelReceivedAt] = useState<number | null>(null);

  const channelRef = useRef<ReturnType<SupabaseClient['channel']> | null>(null);

  // ─── Hydrate from DB + subscribe to channel ────────────────────────────────
  useEffect(() => {
    if (!enabled) return;
    if (!sessionId) return;

    let cancelled = false;

    // 1. Initial hydrate — pull every persisted message for this session
    //    so the chat scroller is populated BEFORE we subscribe to live
    //    broadcasts. RLS gates this to the right requester (either
    //    is_active_team_member() for the authed agent client, or
    //    x-assist-token matching for the anon /assist page client).
    (async () => {
      const { data, error } = await client
        .from('customer_assist_messages')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });
      if (cancelled) return;
      if (error) {
        // Surface to the console; the UI continues with an empty
        // history, broadcast still delivers new messages.
        console.warn('[assist] hydrate failed:', error.message);
        return;
      }
      const rows = (data ?? []) as PersistedRow[];
      setMessages((prev) => {
        // Merge with anything that arrived via broadcast between mount
        // and hydrate. Deduplicate by id.
        const out: AssistChatMessage[] = [];
        const seen = new Set<string>();
        for (const r of rows) {
          if (seen.has(r.id)) continue;
          seen.add(r.id);
          out.push(rowToMessage(r));
        }
        for (const m of prev) {
          if (seen.has(m.id)) continue;
          seen.add(m.id);
          out.push(m);
        }
        return out;
      });
    })();

    // 2. Subscribe to the broadcast + presence channel.
    const channelName = `assist:${sessionId}`;
    const channel = client.channel(channelName, {
      config: {
        presence: { key: role },
        broadcast: { ack: false, self: false },
      },
    });
    channelRef.current = channel;

    const myPresence: PresencePayload = {
      role,
      display_name,
      joined_at: new Date().toISOString(),
    };

    const computePeers = () => {
      const state = channel.presenceState<PresencePayload>();
      const out: AssistPeer[] = [];
      const seen = new Set<string>();
      for (const key of Object.keys(state)) {
        if (key === role) continue;
        const list = state[key] as PresencePayload[];
        const first = list?.[0];
        if (!first) continue;
        const dedupeKey = `${first.role}:${first.display_name}`;
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);
        out.push({
          role: first.role,
          display_name: first.display_name,
          joined_at: first.joined_at,
        });
      }
      setPeers(out);
    };

    channel
      .on('presence', { event: 'sync' }, computePeers)
      .on('presence', { event: 'join' }, computePeers)
      .on('presence', { event: 'leave' }, computePeers)
      .on('broadcast', { event: 'msg' }, ({ payload }: { payload: BroadcastPayload }) => {
        if (payload.from === role) return; // self-suppression

        switch (payload.kind) {
          case 'chat': {
            setMessages((prev) =>
              mergeUniqueById(prev, {
                id: payload.id,
                from: payload.from,
                text: payload.text,
                attachments: Array.isArray(payload.attachments) ? payload.attachments : [],
                ts: payload.ts,
              }),
            );
            break;
          }
          case 'code_request': {
            // Show the masked-input affordance on the receiving side.
            setCodeRequest({ id: payload.id, label: payload.label, ts: payload.ts });
            // Also persist the audit line in the receiver's local view
            // so the chat history shows "[requested X — value not
            // retained]" inline.
            setMessages((prev) =>
              mergeUniqueById(prev, {
                id: payload.id,
                from: payload.from,
                text: payload.text,
                attachments: [],
                ts: payload.ts,
              }),
            );
            break;
          }
          case 'code': {
            // Surface the actual value for 60s. NOT added to messages —
            // the value never enters chat history.
            setSensitiveReceived({
              id: payload.id,
              value: payload.value,
              receivedAt: Date.now(),
            });
            // Add the audit row to messages so the receiver sees
            // "[code shared — not retained]" in the transcript.
            setMessages((prev) =>
              mergeUniqueById(prev, {
                id: payload.id,
                from: payload.from,
                text: payload.text,
                attachments: [],
                ts: payload.ts,
              }),
            );
            break;
          }
          case 'end': {
            // Hard end — clear any pending countdown and flip ended so
            // both UIs render the "Session closed" surface.
            setEndingAt(null);
            setEndingFrom(null);
            setEnded(true);
            break;
          }
          case 'ending': {
            // Other side is initiating an end. Mirror the endsAt so
            // our countdown stays in sync with theirs even if our
            // wall clocks drift slightly.
            setEndingAt(payload.endsAt);
            setEndingFrom(payload.from);
            break;
          }
          case 'end_cancel': {
            // Other side bailed on the end. Clear pending UI state +
            // bump the cancel-received timestamp so listeners that
            // need to react (e.g., the agent's grace-window timer)
            // can detect this distinctly from "endingAt was always
            // null."
            setEndingAt(null);
            setEndingFrom(null);
            setEndCancelReceivedAt(Date.now());
            break;
          }
        }
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          setConnected(true);
          await channel.track(myPresence);
        }
      });

    return () => {
      cancelled = true;
      void channel.untrack().finally(() => {
        void client.removeChannel(channel);
      });
      channelRef.current = null;
      setConnected(false);
      setPeers([]);
    };
  }, [client, sessionId, role, display_name, enabled]);

  // ─── Sensitive-message auto-clear after TTL ────────────────────────────────
  useEffect(() => {
    if (!sensitiveReceived) return;
    const elapsed = Date.now() - sensitiveReceived.receivedAt;
    const remaining = Math.max(0, SENSITIVE_TTL_MS - elapsed);
    const t = setTimeout(() => setSensitiveReceived(null), remaining);
    return () => clearTimeout(t);
  }, [sensitiveReceived?.id, sensitiveReceived?.receivedAt]);

  // ─── Send helpers ──────────────────────────────────────────────────────────
  const broadcast = useCallback((payload: BroadcastPayload) => {
    const c = channelRef.current;
    if (!c) return;
    void c.send({ type: 'broadcast', event: 'msg', payload });
  }, []);

  // Persist a chat-history row first, then broadcast. The receiver
  // dedupes by id so it doesn't matter which order they arrive in.
  const persistAndBroadcast = useCallback(
    async (input: {
      body: string;
      attachments?: AttachmentMeta[];
      kind: 'chat' | 'code_request' | 'code';
      label?: string;
      value?: string;
    }): Promise<AssistChatMessage | null> => {
      if (!sessionId) return null;
      const attachments = input.attachments ?? [];
      const { data, error } = await client
        .from('customer_assist_messages')
        .insert({
          session_id: sessionId,
          from_role: role,
          body: input.body,
          attachments: attachments as unknown as object,
        })
        .select()
        .single();
      if (error) {
        console.error('[assist] persist failed:', error.message);
        throw new Error(error.message);
      }
      const row = data as PersistedRow;
      const local = rowToMessage(row);
      setMessages((prev) => mergeUniqueById(prev, local));

      // Broadcast the matching payload.
      if (input.kind === 'chat') {
        broadcast({
          kind: 'chat',
          id: row.id,
          from: role,
          text: row.body,
          attachments,
          ts: row.created_at,
        });
      } else if (input.kind === 'code_request') {
        broadcast({
          kind: 'code_request',
          id: row.id,
          from: role,
          label: input.label ?? 'verification code',
          text: row.body,
          ts: row.created_at,
        });
      } else if (input.kind === 'code') {
        broadcast({
          kind: 'code',
          id: row.id,
          from: role,
          value: input.value ?? '',
          text: row.body,
          ts: row.created_at,
        });
      }

      return local;
    },
    [broadcast, client, role, sessionId],
  );

  return useMemo<UseAssistChannelResult>(
    () => ({
      peers,
      messages,
      codeRequest,
      sensitiveReceived,
      connected,
      ended,
      sending,
      endingAt,
      endingFrom,
      endCancelReceivedAt,
      async sendChat({ text, attachments }) {
        const body = (text ?? '').trim();
        const atts = attachments ?? [];
        if (!body && atts.length === 0) return;
        setSending(true);
        try {
          await persistAndBroadcast({ body, attachments: atts, kind: 'chat' });
        } finally {
          setSending(false);
        }
      },
      async sendCodeRequest(label: string) {
        const l = label.trim() || 'verification code';
        const auditText = `[requested ${l} — value not retained]`;
        setSending(true);
        try {
          await persistAndBroadcast({ body: auditText, kind: 'code_request', label: l });
        } finally {
          setSending(false);
        }
      },
      async sendCode(value: string) {
        const v = value.trim();
        if (!v) return;
        setCodeRequest(null);
        const auditText = '[code shared — not retained]';
        setSending(true);
        try {
          await persistAndBroadcast({ body: auditText, kind: 'code', value: v });
        } finally {
          setSending(false);
        }
      },
      sendEnding(graceMs: number) {
        const ts = new Date().toISOString();
        const endsAt = Date.now() + Math.max(0, graceMs);
        broadcast({ kind: 'ending', from: role, endsAt, ts });
        // We DON'T mirror endingAt into local state here — the caller
        // (provider) owns the agent-side pending state. Mirroring it
        // would create two sources of truth for the same value.
      },
      sendEndCancel() {
        const ts = new Date().toISOString();
        broadcast({ kind: 'end_cancel', from: role, ts });
        // Clear our local mirror too in case we previously received an
        // `ending` from the other side and are now cancelling on
        // their behalf.
        setEndingAt(null);
        setEndingFrom(null);
      },
      sendEnd() {
        const ts = new Date().toISOString();
        broadcast({ kind: 'end', from: role, ts });
        setEndingAt(null);
        setEndingFrom(null);
        setEnded(true);
      },
    }),
    [peers, messages, codeRequest, sensitiveReceived, connected, ended, sending, endingAt, endingFrom, endCancelReceivedAt, persistAndBroadcast, broadcast, role],
  );
}
